(() => {
  const dataRequests = new Map();
  const homePageSize = 8;
  const productPageSize = 6;

  function getReviews(url) {
    if (!dataRequests.has(url)) {
      dataRequests.set(url, fetch(url, { credentials: 'same-origin' }).then((response) => {
        if (!response.ok) throw new Error(`Review data request failed: ${response.status}`);
        return response.json();
      }));
    }
    return dataRequests.get(url);
  }

  function dateLabel(value) {
    return String(value || '').slice(0, 10);
  }

  function averageRating(reviews) {
    return reviews.reduce((total, review) => total + Number(review.rating || 0), 0) / reviews.length;
  }

  function setRating(target, rating, productCard) {
    const roundedRating = Math.max(0, Math.min(5, Number(rating) || 0));
    const stars = '★'.repeat(Math.floor(roundedRating)) + '☆'.repeat(5 - Math.floor(roundedRating));
    target.textContent = stars;
    target.setAttribute('role', 'img');
    target.setAttribute('aria-label', `${roundedRating} out of 5 stars`);
    if (productCard) target.classList.add('hp__stars');
  }

  function productHref(review) {
    return `/products/${encodeURIComponent(review.product_handle || '')}`;
  }

  function addPhotos(card, review, className) {
    const photos = (Array.isArray(review.photos) ? review.photos : []).filter((photo) => photo && photo.url);
    if (!photos.length) return;
    const container = document.createElement('div');
    container.className = className;
    photos.forEach((photo) => {
      const image = document.createElement('img');
      image.src = photo.url;
      image.alt = photo.alt || 'Customer photo attached to an imported review';
      image.loading = 'lazy';
      image.decoding = 'async';
      container.append(image);
    });
    card.append(container);
  }

  function addProductLink(card, review, className) {
    const link = document.createElement('a');
    link.className = className;
    link.href = productHref(review);
    link.textContent = review.product_name || 'View product';
    card.append(link);
  }

  function addSourceMeta(target, review) {
    const name = String(review.display_name || '').trim();
    const date = dateLabel(review.date);
    const sourceName = String(review.source_name || 'Supplier platform').trim();
    let source = document.createElement('span');
    source.textContent = `Imported from ${sourceName}`;
    try {
      const sourceUrl = new URL(review.source_url || '');
      if (sourceUrl.protocol === 'https:') {
        source = document.createElement('a');
        source.textContent = `Imported from ${sourceName}`;
        source.href = sourceUrl.href;
        source.target = '_blank';
        source.rel = 'noopener noreferrer';
      }
    } catch (error) {
      source.textContent = `Imported from ${sourceName}`;
    }
    target.replaceChildren(document.createTextNode([name, date].filter(Boolean).join(' · ')));
    if (name || date) target.append(document.createTextNode(' · '));
    target.append(source);
  }

  function createHomeCard(root, review, position, index) {
    const template = root.querySelector('[data-halloween-review-card-template]');
    if (!template) return null;
    const fragment = template.content.cloneNode(true);
    const card = fragment.querySelector('article');
    card.classList.add(`halloween-reviews__card--${position}`);
    if (index % 3 === 2) card.classList.add('halloween-reviews__card--lilac');
    const decoration = card.querySelector('.halloween-reviews__decoration');
    if (position !== 'featured' && index % 3 !== 2) decoration.remove();

    setRating(card.querySelector('[data-review-stars]'), review.rating, false);
    const title = card.querySelector('[data-review-title]');
    if (String(review.title || '').trim()) {
      title.textContent = review.title;
      title.hidden = false;
    } else {
      title.remove();
    }
    card.querySelector('[data-review-text]').textContent = review.text || '';
    addPhotos(card, review, 'halloween-reviews__photos');
    const meta = card.querySelector('[data-review-meta]');
    addSourceMeta(meta, review);
    const productWrapper = card.querySelector('[data-review-product-wrap]');
    const productLink = card.querySelector('[data-review-product]');
    productLink.href = productHref(review);
    productLink.textContent = review.product_name || 'View product';
    productWrapper.hidden = false;
    return fragment;
  }

  function createProductCard(review) {
    const card = document.createElement('article');
    card.className = 'hp__review';
    const stars = document.createElement('div');
    setRating(stars, review.rating, true);
    card.append(stars);
    if (String(review.title || '').trim()) {
      const title = document.createElement('h3');
      title.textContent = review.title;
      card.append(title);
    }
    const body = document.createElement('p');
    body.textContent = review.text || '';
    card.append(body);
    addPhotos(card, review, 'hp__review-photos');
    addProductLink(card, review, 'hp__review-product');
    const meta = document.createElement('small');
    addSourceMeta(meta, review);
    card.append(meta);
    return card;
  }

  function summaryText(reviews) {
    const label = reviews.length === 1 ? 'review' : 'reviews';
    return `${reviews.length} imported ${label} · ${averageRating(reviews).toFixed(1)}/5 average · supplier-platform feedback`;
  }

  function showHome(root, reviews) {
    const top = root.querySelector('.halloween-reviews__top');
    const middle = root.querySelector('[data-review-middle]');
    const bottom = root.querySelector('[data-review-bottom]');
    const more = root.querySelector('[data-review-more]');
    const batchSize = homePageSize;
    let rendered = 0;

    root.querySelector('[data-review-summary]').textContent = summaryText(reviews);

    function renderBatch(initial) {
      const batch = reviews.slice(rendered, rendered + batchSize);
      batch.forEach((review) => {
        let position = 'bottom';
        let destination = bottom;
        if (initial && rendered === 0) {
          position = 'featured';
          destination = top;
        } else if (initial && rendered < 4) {
          position = 'middle';
          destination = middle;
        }
        destination.append(createHomeCard(root, review, position, rendered));
        rendered += 1;
      });
      more.hidden = rendered >= reviews.length;
    }

    renderBatch(true);
    more.addEventListener('click', () => renderBatch(false));
    root.hidden = false;
  }

  function showProduct(root, reviews) {
    const grid = root.querySelector('[data-review-grid]');
    const more = root.querySelector('[data-review-more]');
    let rendered = 0;
    root.querySelector('[data-review-summary]').textContent = summaryText(reviews);

    function renderBatch() {
      const batch = reviews.slice(rendered, rendered + productPageSize);
      batch.forEach((review) => {
        grid.append(createProductCard(review));
        rendered += 1;
      });
      more.hidden = rendered >= reviews.length;
    }

    renderBatch();
    more.addEventListener('click', renderBatch);
    root.hidden = false;
  }

  async function initialize(root) {
    if (root.dataset.reviewReady === 'true') return;
    root.dataset.reviewReady = 'loading';
    try {
      const data = await getReviews(root.dataset.reviewUrl);
      let reviews = Array.isArray(data.reviews) ? data.reviews.slice() : [];
      if (root.dataset.reviewLayout === 'product') {
        reviews = reviews.filter((review) => review.product_handle === root.dataset.reviewHandle);
        if (reviews.length) showProduct(root, reviews);
      } else {
        reviews = reviews.filter((review) => ['animated-eyeball-doorbell-make-every-visitor-scream', 'usb-halloween-ghost-skull-projector-instant-haunted-room'].includes(review.product_handle));
        reviews.sort((first, second) => Date.parse(second.date || '') - Date.parse(first.date || ''));
        if (reviews.length) showHome(root, reviews);
      }
      root.dataset.reviewReady = 'true';
    } catch (error) {
      root.dataset.reviewReady = 'error';
      root.hidden = true;
      console.error('Halloween reviews could not be rendered.', error);
    }
  }

  function initializeAll() {
    document.querySelectorAll('[data-halloween-reviews]').forEach(initialize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  } else {
    initializeAll();
  }
  document.addEventListener('shopify:section:load', initializeAll);
})();
